import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { toast } from "sonner";
import { AlertCircle, Info } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchActiveShiftsForCompany, fetchActiveEmployeeShift } from "@/services/employeeShiftService";
import { ShiftSelector } from "@/components/common/ShiftSelector";
import { validateEmployeeForm, splitFullName } from "@/utils/formValidation";
import { ModalActions } from "@/components/common/ModalActions";
import { Shift } from "@/types/index";
import { useDutyRosterManagement } from "@/hooks/useDutyRosterManagement";
import { normalizeResidency, getResidencyOptions, getResidencyLabel } from '@/utils/residencyUtils';

interface Department {
  id: string;
  name: string;
  company_id: string;
}

interface EmployeeFormData {
  id?: string;
  name: string;
  email: string;
  employee_id?: string;
  department: string;
  position: string;
  residency: string;
  workerType: string;
  shift?: string;
  is_duty_roster?: boolean;
  group_id?: string;
}

interface EmployeeModalProps {
  mode: 'add' | 'edit';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee?: EmployeeFormData;
  onSave: (employee: EmployeeFormData, shiftAssignment?: { shiftId: string; fromDate: string; toDate?: string }) => void;
  departments?: Department[];
  isLoadingDepartments?: boolean;
  acceptedDomains?: string[];
}

/**
 * New simplified employee modal for both adding and editing employees
 * Eliminates duplicate key issues and focuses on core functionality
 */
export function EmployeeModal({
  mode,
  open,
  onOpenChange,
  employee,
  onSave,
  departments = [],
  isLoadingDepartments = false,
  acceptedDomains = []
}: EmployeeModalProps) {
  const { user } = useAuth();
  
  // Duty roster management hook
  const { groups: dutyRosterGroups } = useDutyRosterManagement(user?.companyId || '');
  
  const [formData, setFormData] = useState<EmployeeFormData>({
    name: '',
    email: '',
    employee_id: '',
    department: '',
    position: '',
    residency: '',
    workerType: '',
    is_duty_roster: false,
    group_id: ''
  });
  
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Shift assignment state (only for add mode)
  const [needsShiftAssignment, setNeedsShiftAssignment] = useState(false);
  const [selectedShift, setSelectedShift] = useState<string>('');

  // Filter groups by residency type for validation
  const getFilteredGroups = () => {
    if (!formData.residency) return dutyRosterGroups;
    
    const normalizedEmployeeResidency = normalizeResidency(formData.residency);
    // Normalize both employee and group residencies for comparison (same logic as DutyRosterEmployeesList)
    return dutyRosterGroups.filter(group => {
      const normalizedGroupResidency = normalizeResidency(group.residency_type);
      return normalizedGroupResidency === normalizedEmployeeResidency;
    });
  };

  // Initialize form data
  useEffect(() => {
    if (mode === 'edit' && employee) {
      setFormData({
        id: employee.id,
        name: employee.name,
        email: employee.email,
        employee_id: employee.employee_id || '',
        department: employee.department,
        position: employee.position,
        residency: employee.residency || '',
        workerType: employee.workerType || '',
        shift: employee.shift,
        is_duty_roster: employee.is_duty_roster || false,
        group_id: employee.group_id || ''
      });
      setSelectedShift(employee.shift || '');
    } else if (mode === 'add') {
      // Reset for add mode
      setFormData({
        name: '',
        email: '',
        employee_id: '',
        department: '',
        position: '',
        residency: '',
        workerType: '',
        is_duty_roster: false,
        group_id: ''
      });
      setNeedsShiftAssignment(false);
      setSelectedShift('');
    }
    setEmailError(null);
  }, [mode, employee, open]);

  // Fetch active shift assignment for edit mode
  useEffect(() => {
    const fetchEmployeeShiftAssignment = async () => {
      if (mode === 'edit' && employee?.id && companyId) {
        try {
          const activeShift = await fetchActiveEmployeeShift(employee.id);
          if (activeShift && activeShift.shifts) {
            // Update form data with the active shift assignment
            // Use shift ID for proper selection in the dropdown
            const shiftValue = activeShift.shifts.id;
            setFormData(prev => ({
              ...prev,
              shift: shiftValue
            }));
            setSelectedShift(shiftValue);
          }
        } catch (error) {
          console.error('Error fetching employee shift assignment:', error);
        }
      }
    };

    fetchEmployeeShiftAssignment();
  }, [mode, employee?.id, companyId]);

  // Set company ID for shift fetching
  useEffect(() => {
    if (!open || !user?.companyId) return;
    setCompanyId(user.companyId);
  }, [open, user?.companyId]);

  // Fetch unique residency values from employees table
  const { data: residencyOptions = [] } = useQuery({
    queryKey: ['residency_options', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from('employees')
        .select('residency')
        .eq('company_id', companyId)
        .not('residency', 'is', null)
        .order('residency');
      if (error) throw error;
      // Get unique values
      const uniqueResidencies = [...new Set(data?.map(emp => emp.residency).filter(Boolean))];
      return uniqueResidencies;
    },
    enabled: !!companyId,
  });

  // Fetch unique worker_type values from employees table
  const { data: workerTypeOptions = [] } = useQuery({
    queryKey: ['worker_type_options', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from('employees')
        .select('worker_type')
        .eq('company_id', companyId)
        .not('worker_type', 'is', null)
        .order('worker_type');
      if (error) throw error;
      // Get unique values
      const uniqueWorkerTypes = [...new Set(data?.map(emp => emp.worker_type).filter(Boolean))];
      return uniqueWorkerTypes;
    },
    enabled: !!companyId,
  });





  // Set values when editing and options are loaded
  useEffect(() => {
    if (mode === 'edit' && employee && residencyOptions.length > 0 && workerTypeOptions.length > 0) {
      setFormData(prev => ({
        ...prev,
        residency: employee.residency || '',
        workerType: employee.workerType || ''
      }));
    }
  }, [mode, employee, residencyOptions, workerTypeOptions]);

  // Fetch shifts when needed
  useEffect(() => {
    if (companyId) {
      fetchActiveShiftsForCompany(companyId).then(setShifts);
    }
  }, [companyId]);



  // No need for complex deduplication - just use the original shifts array
  // React keys will be unique since we use shift.id

  // Enhanced email validation with domain checking
  const validateEmailWithDomains = (email: string): boolean => {
    if (!email.trim()) return true; // Email is optional
    
    // Use shared validation for basic format check
    const basicValidation = validateEmployeeForm({ name: '', email, department: '' });
    if (basicValidation.errors.email) {
      setEmailError(basicValidation.errors.email);
      return false;
    }
    
    if (acceptedDomains.length === 0) {
      setEmailError(null);
      return true;
    }
    
    const domain = email.split('@')[1];
    if (!domain || !acceptedDomains.includes(domain)) {
      setEmailError(`Email domain must be one of: ${acceptedDomains.join(', ')}`);
      return false;
    }
    
    setEmailError(null);
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Basic validation
      if (!formData.name.trim() || !formData.department.trim()) {
        toast.error("Please fill all required fields (Full Name, Department)");
        return;
      }
      
      if (formData.email.trim() && !validateEmailWithDomains(formData.email)) {
        return;
      }

      // Validate shift assignment for add mode (only if not duty roster)
      if (mode === 'add' && !formData.is_duty_roster && needsShiftAssignment && !selectedShift) {
        toast.error("Please select a shift for assignment");
        return;
      }

      // Validate duty roster group assignment for add mode
      if (mode === 'add' && formData.is_duty_roster && !formData.group_id) {
        toast.error("Please select a duty roster group");
        return;
      }

      // Validate residency-group compatibility
      if (formData.is_duty_roster && formData.group_id && formData.residency) {
        const selectedGroup = dutyRosterGroups.find(g => g.id === formData.group_id);
        if (selectedGroup && normalizeResidency(selectedGroup.residency_type) !== normalizeResidency(formData.residency)) {
          toast.error(`Selected group is for ${selectedGroup.residency_type} employees, but employee is ${formData.residency}`);
          return;
        }
      }

      if (mode === 'add') {
        // Add mode - split name and create employeex
        const { firstName, lastName } = splitFullName(formData.name);
        
        const employeePayload = {
          name: formData.name,
          firstName,
          lastName,
          email: formData.email || '',
          employee_id: formData.employee_id || undefined,
          department: formData.department,
          position: formData.position,
          residency: formData.residency,
          workerType: formData.workerType,
          companyId,
          is_duty_roster: formData.is_duty_roster,
          group_id: formData.is_duty_roster ? formData.group_id : undefined
        };

        // Only assign shift if not duty roster
        const shiftAssignmentData = (!formData.is_duty_roster && needsShiftAssignment) ? {
          shiftId: selectedShift,
          fromDate: new Date().toISOString().split('T')[0],
          toDate: undefined
        } : undefined;
        


        onSave(employeePayload, shiftAssignmentData);
      } else {
        // Edit mode - handle duty roster switching logic
        const originalData = employee!;
        const changedData: Partial<EmployeeFormData> = {};
        
        // Check for duty roster status change
        const dutyRosterChanged = formData.is_duty_roster !== originalData.is_duty_roster;
        
        if (dutyRosterChanged) {
          if (formData.is_duty_roster) {
            // Switching from False -> True: Let database handle group_id
            changedData.is_duty_roster = true;
            // Don't set group_id here - let database function handle it
          } else {
            // Switching from True -> False: Clear group_id
            changedData.is_duty_roster = false;
            changedData.group_id = null;
          }
        } else {
          // No duty roster status change, check other fields
          Object.keys(formData).forEach(key => {
            const formKey = key as keyof EmployeeFormData;
            if (formData[formKey] !== originalData[formKey]) {
              (changedData as Record<string, unknown>)[formKey] = formData[formKey];
            }
          });
        }

        if (Object.keys(changedData).length > 0) {
          onSave({
            id: formData.id,
            name: formData.name,
            email: formData.email,
            employee_id: formData.employee_id,
            department: formData.department,
            position: formData.position,
            residency: formData.residency,
            workerType: formData.workerType,
            ...changedData
          });
        } else {
          toast.info("No changes detected.");
          onOpenChange(false);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while processing your request');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form field handlers
  const handleInputChange = (field: keyof EmployeeFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'email' && value) {
      validateEmailWithDomains(value);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add' ? 'Add New Employee' : 'Edit Employee'}
          </DialogTitle>
        </DialogHeader>
        

        
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
          className="space-y-4 pt-4"
        >
          {/* Basic Information */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Full Name *</Label>
              <Input 
                id="name" 
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                placeholder="Enter full name"
                className="h-9"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input 
                id="email" 
                type="email"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                placeholder={mode === 'add' ? "Enter email (optional)" : "Email"}
                className={`h-9 ${emailError ? "border-red-500" : ""}`}
              />
              {emailError && (
                <div className="flex items-center gap-1 text-destructive text-xs">
                  <AlertCircle className="h-3 w-3" />
                  <span>{emailError}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="employee_id" className="text-sm font-medium">Employee ID</Label>
              <Input 
                id="employee_id" 
                value={formData.employee_id}
                onChange={e => handleInputChange('employee_id', e.target.value)}
                placeholder="Enter employee ID"
                className="h-9"
              />
            </div>
          </div>

          {/* Position, Department, Assign Shift Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="position" className="text-sm font-medium">Position</Label>
              <Input 
                id="position" 
                value={formData.position}
                onChange={e => handleInputChange('position', e.target.value)}
                placeholder="Enter position"
                className="h-9"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department" className="text-sm font-medium">Department *</Label>
              <Select 
                value={formData.department} 
                onValueChange={value => handleInputChange('department', value)}
                disabled={isLoadingDepartments || departments.length === 0}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={
                    isLoadingDepartments ? "Loading..." : 
                    departments.length === 0 ? "No departments available" : 
                    "Select department"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assign Shift Toggle - Only show if not duty roster */}
            {!formData.is_duty_roster && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Assign Shift *</Label>
                <div className="flex items-center space-x-2 h-9">
                  <Switch
                    checked={mode === 'add' ? needsShiftAssignment : !!formData.shift}
                    onCheckedChange={(checked) => {
                      if (mode === 'add') {
                        setNeedsShiftAssignment(checked);
                      }
                      // For edit mode, the toggle reflects the current shift assignment
                    }}
                    disabled={mode === 'edit'} // Disable toggle in edit mode, just show current state
                  />
                  <span className="text-sm text-muted-foreground">
                    {mode === 'add' ? (needsShiftAssignment ? "Yes" : "No") : (formData.shift ? "Yes" : "No")}
                  </span>
                  {mode === 'edit' && (
                    <div className="relative group">
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-gray-900 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Update from Shifts tab
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Residency, Worker Type, Shift Selection (Add mode with shift enabled) */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="residency" className="text-sm font-medium">Residency</Label>
              <Select 
                value={formData.residency || ''}
                onValueChange={value => handleInputChange('residency', value)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select residency" />
                </SelectTrigger>
                <SelectContent>
                  {residencyOptions.length > 0 ? (
                    residencyOptions.map((residency) => (
                      <SelectItem key={residency} value={residency}>
                        {residency}
                      </SelectItem>
                    ))
                  ) : (
                    getResidencyOptions().map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="workerType" className="text-sm font-medium">Worker Type</Label>
              <Select 
                value={formData.workerType}
                onValueChange={value => handleInputChange('workerType', value)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select worker type" />
                </SelectTrigger>
                <SelectContent>
                  {workerTypeOptions.length > 0 ? (
                    workerTypeOptions.map((workerType) => (
                      <SelectItem key={workerType} value={workerType}>
                        {workerType}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="full_time">Full-time</SelectItem>
                      <SelectItem value="part_time">Part-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="temporary">Temporary</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Shift Selection - Only show if not duty roster and shift is needed/assigned */}
            {!formData.is_duty_roster && ((mode === 'add' && needsShiftAssignment) || (mode === 'edit' && formData.shift)) && shifts.length > 0 ? (
              <ShiftSelector
                shifts={shifts.filter(shift => shift.in_working_hours === false)} // Filter to only show non-duty roster shifts
                selectedShiftId={mode === 'add' ? selectedShift : formData.shift}
                onShiftChange={mode === 'add' ? setSelectedShift : (value) => handleInputChange('shift', value)}
                label="Select Shift *"
                placeholder="Select shift"
                className="w-full"
                uniqueId={mode === 'add' ? "employee-modal-add" : "employee-modal-edit"}
                useShiftCodeAsValue={false}
                showShiftInfo={false}
                disabled={mode === 'edit'}
              />
            ) : !formData.is_duty_roster ? (
              // Show empty div to maintain grid layout when no shift is selected
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Shift</Label>
                <div className="h-9 bg-muted/20 rounded-md flex items-center px-3 text-sm text-muted-foreground">
                  No shift assigned
                </div>
              </div>
            ) : (
              // Show duty roster info when duty roster is enabled
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Assignment</Label>
                <div className="h-9 bg-blue-50 dark:bg-blue-950/20 rounded-md flex items-center px-3 text-sm text-blue-700 dark:text-blue-300">
                  Duty Roster Employee
                </div>
              </div>
            )}

            
          </div>

          {/* Shift Information Display - Only show if not duty roster and shift is selected */}
          {!formData.is_duty_roster && ((mode === 'add' && needsShiftAssignment && selectedShift) || (mode === 'edit' && formData.shift)) && (
            <div className="space-y-2 w-full">
              <ShiftSelector
                shifts={shifts.filter(s => s.id === (mode === 'add' ? selectedShift : formData.shift) && s.in_working_hours === false)} // Filter to only show non-duty roster shifts
                selectedShiftId={mode === 'add' ? selectedShift : formData.shift}
                onShiftChange={() => {}} // Read-only for info display
                showShiftInfo={true}
                label=""
                className="[&>div:first-child]:hidden w-full" // Hide the dropdown, only show info, use full width
                uniqueId={mode === 'add' ? "employee-modal-info" : "employee-modal-edit-info"}
                useShiftCodeAsValue={false}
              />
            </div>
          )}

          {/* Duty Roster Assignment */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_duty_roster || false}
                onCheckedChange={(checked) => {
                  if (mode === 'edit') {
                    // Edit mode: Handle duty roster status change
                    setFormData(prev => ({ 
                      ...prev, 
                      is_duty_roster: checked,
                      // Clear group_id when switching to false, keep it when switching to true
                      group_id: checked ? prev.group_id : null
                    }));
                  } else {
                    // Add mode: Normal behavior
                    setFormData(prev => ({ 
                      ...prev, 
                      is_duty_roster: checked,
                      group_id: checked ? prev.group_id : ''
                    }));
                  }
                }}
              />
              <Label className="text-sm font-medium">Follow Duty Roster Schedule</Label>
            </div>

            {formData.is_duty_roster && mode === 'add' && (
              <div className="space-y-4">
                {/* Residency validation warning */}
                {formData.residency && getFilteredGroups().length === 0 && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">No groups available</span>
                    </div>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      No duty roster groups found for {getResidencyLabel(formData.residency)} employees. Please create a group first or change the residency type.
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="group_id" className="text-sm font-medium">Duty Roster Group *</Label>
                    <Select 
                      value={formData.group_id}
                      onValueChange={value => handleInputChange('group_id', value)}
                      disabled={getFilteredGroups().length === 0}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={
                          getFilteredGroups().length === 0 ? "No groups available" : "Select duty roster group"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {getFilteredGroups().map(group => (
                          <SelectItem key={group.id} value={group.id}>
                            {group.group_name} ({getResidencyLabel(group.residency_type)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">Residency Type</Label>
                    <div className="h-9 bg-muted/20 rounded-md flex items-center px-3 text-sm text-muted-foreground">
                      {formData.residency ? getResidencyLabel(formData.residency) : 'Not specified'}
                    </div>
                  </div>
                </div>
                
              </div>
            )}
          </div>
        </form>

        <ModalActions
          onCancel={() => onOpenChange(false)}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          submitLabel={mode === 'add' ? "Create Employee" : "Save Changes"}
          submitVariant="secondary"
          submitDisabled={
            !formData.name.trim() || 
            !formData.department.trim() || 
            (mode === 'add' && !formData.is_duty_roster && needsShiftAssignment && !selectedShift) || 
            (mode === 'add' && formData.is_duty_roster && !formData.group_id) ||
            (mode === 'add' && formData.is_duty_roster && formData.residency && getFilteredGroups().length === 0)
          }
        />
      </DialogContent>
    </Dialog>
  );
}
